import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  notification, 
  Input, 
  Pagination, 
  Empty, 
  Segmented, 
  Button, 
  Typography, 
  Avatar, 
  Card, 
  Row, 
  Col, 
  Space, 
  Badge, 
  Spin,
  Tooltip,
  Statistic
} from 'antd';
import { myRequestObj } from '../../assets/utils/lib';
import { uri } from '../../assets/utils/http-request';
import config from '../../assets/utils/config';
import { NullSafe } from '../../assets/utils/Helpers';
import CurrencyFormat from 'react-currency-format';
import Loader from '../compoinents/loader';
import { 
  FileTextOutlined, 
  BankOutlined, 
  CheckOutlined, 
  DollarOutlined, 
  SearchOutlined, 
  ReloadOutlined, 
  EnvironmentOutlined, 
  MailOutlined, 
  PhoneOutlined, 
  TeamOutlined,
  EyeOutlined,
  CalendarOutlined,
  UserOutlined,
  Loading3QuartersOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { RiHospitalFill } from 'react-icons/ri';
import StandardFilter from '../compoinents/StandardFilter';
import { useStandardFilter } from '../compoinents/useStandardFilter';
import { CLAIMS_FILTERS } from '../compoinents/FilterConfigs';

// Import the modern SCSS styles
import '../../styles/main.scss';

const { Title, Text } = Typography;

// Enhanced status configuration with better visual indicators
const STATUS_CONFIG = {
  0: { 
    text: 'Pending CO', 
    color: '#121221', 
    icon: <ClockCircleOutlined />,
    bgColor: '#f0f0f0',
    description: 'Claims pending CO review'
  },
  1: { 
    text: 'Pending HM', 
    color: '#ff9200', 
    icon: <Loading3QuartersOutlined />,
    bgColor: '#fff7e6',
    description: 'Claims pending HM approval'
  },
  2: { 
    text: 'Pending CFO', 
    color: '#afaa0b', 
    icon: <ExclamationCircleOutlined />,
    bgColor: '#fffbe6',
    description: 'Claims pending CFO approval'
  },
  3: { 
    text: 'Paid', 
    color: '#00aa00', 
    icon: <CheckCircleOutlined />,
    bgColor: '#f6ffed',
    description: 'Claims that have been paid'
  },
  // 4: { 
  //   text: 'Denied', 
  //   color: '#ff4d4f', 
  //   icon: <CloseCircleOutlined />,
  //   bgColor: '#fff2f0',
  //   description: 'Claims that have been denied'
  // }
};

// Modern Facility Card Component
const FacilityCard = ({ facility, activeSegment, onClick }) => {
  // Handle paid segment data structure
  const isPaidSegment = activeSegment === 3;
  // const facilityName = isPaidSegment ? facility.name : facility.facility_name;
  // const facilityAddress = isPaidSegment ? facility.address : facility.facility_address;
  // const facilityState = isPaidSegment ? facility.state : facility.facility_state;
  // const facilityCity = isPaidSegment ? facility.city : facility.facility_city;
  // const facilityPhone = isPaidSegment ? facility.phone : facility.facility_phone;
  // const facilityEmail = isPaidSegment ? facility.email : facility.facility_email;

  const facilityName = facility.facility_name;
  const facilityAddress = facility.facility_address;
  const facilityState = facility.facility_state;
  const facilityCity = facility.facility_city;
  const facilityPhone = facility.facility_phone;
  const facilityEmail = facility.facility_email;
  
  // Handle statistics for paid segment
  const claimsCount = isPaidSegment ? facility.claims?.length || 0 : facility.claims_count;
  const paCount = isPaidSegment ? 0 : facility.pa_count;
  const totalCost = isPaidSegment ? 
    facility.claims?.reduce((prev, curr) => prev + Number(curr.encounter_cost), 0) || 0 : 
    facility.pass || 0;
  const approvedCost = isPaidSegment ?
    facility.claims?.reduce((prev, curr) => prev + Number(curr.approved_total ?? curr.approved_cost ?? 0), 0) || 0 :
    0;
  const uniqueInsuranceCount = isPaidSegment ? 
    facility.claims?.length || 0 : 
    facility.unique_insurance_count;
  const lastClaimDate = isPaidSegment ? 
    facility.claims?.[0]?.date_paid : 
    facility.last_claim_date;

  // Priority indicator for paid segment
  const priorityIndicator = isPaidSegment && totalCost > 500000 ? {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '4px',
    height: '100%',
    background: 'linear-gradient(180deg, #ff4d4f 0%, #ff7875 100%)',
    borderRadius: '8px 0 0 8px'
  } : null;

  return (
    <Card 
      className="facility-card"
      style={{ 
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(0, 0, 0, 0.05)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden'
      }}
      onClick={onClick}
    >
      {priorityIndicator && <div style={priorityIndicator} />}
      
      <div className="facility-card-content">
        {/* Header Section */}
        <div className="facility-header" style={{ marginBottom: '16px' }}>
          <div className="facility-info">
            <Title level={4} style={{ 
              margin: 0, 
              color: '#1a1a1a',
              fontWeight: 700,
              lineHeight: 1.2,
              fontSize: '16px'
            }}>
              {facilityName}
            </Title>
            <Text style={{ 
              color: '#666', 
              fontSize: '13px', 
              fontWeight: 500,
              marginTop: '4px',
              display: 'block'
            }}>
              {facilityAddress}
            </Text>
          </div>
        </div>

        {/* Contact Information */}
        <div className="facility-contact" style={{ marginBottom: '16px' }}>
          <Row gutter={[12, 12]}>
            <Col span={24}>
              <div className="contact-item">
                <div className="contact-content">
                  <MailOutlined style={{ marginRight: '8px', fontSize: '14px', color: '#666' }} />
                  <Text style={{ color: '#666', fontWeight: 500, fontSize: '13px' }}>
                    {facilityEmail}
                  </Text>
                </div>
              </div>
            </Col>
            <Col span={24}>
              <div className="contact-item">
                <div className="contact-content">
                  <PhoneOutlined style={{ marginRight: '8px', fontSize: '14px', color: '#666' }} />
                  <a 
                    href={`tel:${facilityPhone}`}
                    style={{ 
                      color: '#1890ff', 
                      textDecoration: 'none',
                      fontWeight: 500,
                      fontSize: '13px',
                      transition: 'opacity 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                  >
                    {facilityPhone}
                  </a>
                </div>
              </div>
            </Col>
          </Row>
        </div>

        {/* Statistics Section */}
        <div className="facility-stats" style={{ marginBottom: '16px' }}>
          <Row gutter={[12, 12]}>
            <Col span={12}>
              <div className="stat-item">
                <div className="stat-icon">
                  <FileTextOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
                </div>
                <div className="stat-content">
                  <Statistic 
                    value={claimsCount} 
                    suffix={isPaidSegment ? '' : `/ ${paCount}`}
                    valueStyle={{ 
                      fontSize: '16px', 
                      fontWeight: 700, 
                      color: '#1890ff' 
                    }}
                  />
                  <Text style={{ fontSize: '10px', color: '#666', fontWeight: 600 }}>
                    {isPaidSegment ? 'Claims' : 'Claims / PAR'}
                  </Text>
                </div>
              </div>
            </Col>
            <Col span={12}>
              <div className="stat-item">
                <div className="stat-icon">
                  <DollarOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
                </div>
                <div className="stat-content">
                  <Statistic 
                    value={totalCost}
                    precision={2}
                    prefix="₦"
                    valueStyle={{ 
                      fontSize: '16px', 
                      fontWeight: 700, 
                      color: '#52c41a' 
                    }}
                  />
                  <Text style={{ fontSize: '10px', color: '#666', fontWeight: 600 }}>
                    Total Cost
                  </Text>
                </div>
              </div>
            </Col>
            {isPaidSegment && approvedCost > 0 && (
              <Col span={12}>
                <div className="stat-item">
                  <div className="stat-icon">
                    <CheckOutlined style={{ color: '#722ed1', fontSize: '16px' }} />
                  </div>
                  <div className="stat-content">
                    <Statistic 
                      value={approvedCost}
                      precision={2}
                      prefix="₦"
                      valueStyle={{ 
                        fontSize: '16px', 
                        fontWeight: 700, 
                        color: '#722ed1' 
                      }}
                    />
                    <Text style={{ fontSize: '10px', color: '#666', fontWeight: 600 }}>
                      Approved Cost
                    </Text>
                  </div>
                </div>
              </Col>
            )}
            <Col span={isPaidSegment && approvedCost > 0 ? 12 : 12}>
              <div className="stat-item">
                <div className="stat-icon">
                  <TeamOutlined style={{ color: '#722ed1', fontSize: '16px' }} />
                </div>
                <div className="stat-content">
                  <Statistic 
                    value={uniqueInsuranceCount}
                    valueStyle={{ 
                      fontSize: '16px', 
                      fontWeight: 700, 
                      color: '#722ed1' 
                    }}
                  />
                  <Text style={{ fontSize: '10px', color: '#666', fontWeight: 600 }}>
                    {isPaidSegment ? 'Patients' : 'Users Concerned'}
                  </Text>
                </div>
              </div>
            </Col>
            <Col span={12}>
              <div className="stat-item">
                <div className="stat-icon">
                  <CalendarOutlined style={{ color: '#fa8c16', fontSize: '16px' }} />
                </div>
                <div className="stat-content">
                  <Text style={{ 
                    fontSize: '14px', 
                    fontWeight: 700, 
                    color: '#fa8c16',
                    lineHeight: 1.2
                  }}>
                    {lastClaimDate ? 
                      new Date(lastClaimDate).toLocaleDateString('en-NG', { 
                        month: 'short', 
                        day: 'numeric' ,
                        year: 'numeric'
                      }) : 
                      'N/A'
                    }
                  </Text><br></br>
                  <Text style={{ fontSize: '10px', color: '#666', fontWeight: 600 }}>
                    {isPaidSegment ? 'Payment Date' : 'Last Submission'}
                  </Text>
                </div>
              </div>
            </Col>
          </Row>
        </div>

        {/* Action Button */}
        <div className="facility-actions" style={{ marginTop: '16px', textAlign: 'center' }}>
          <Button 
            type="primary" 
            icon={<EyeOutlined />}
            size="small"
            style={{ 
              borderRadius: '8px',
              fontWeight: 600,
              height: '32px',
              padding: '0 16px',
              boxShadow: '0 2px 8px rgba(24, 144, 255, 0.25)',
              background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
              border: 'none'
            }}
          >
            {isPaidSegment ? 'View Details' : 'View Claims'}
          </Button>
        </div>
      </div>
    </Card>
  );
};

// Modern Payment Batch Card Component
const PaymentBatchCard = ({ batch, activeSegment, onClick }) => {
  // Safely handle cases where claims might be undefined or not an array
  const claims = batch.claims || [];
  
  // Don't render if no claims
  if (!claims.length) return null;
  
  const totalCost = claims.reduce((prev, curr) => prev + Number(curr.encounter_cost || 0), 0);
  const approvedCost = claims.reduce((prev, curr) => prev + Number(curr.approved_total ?? curr.approved_cost ?? 0), 0);
  const uniquePatients = new Set(claims.map(c => c.insurance_no)).size;
  const lastPaymentDate = claims[0]?.date_paid || claims[0]?.claims_date;

  // Priority indicator for high-value batches
  const priorityIndicator = totalCost > 1000000 ? {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '4px',
    height: '100%',
    background: 'linear-gradient(180deg, #ff4d4f 0%, #ff7875 100%)',
    borderRadius: '8px 0 0 8px'
  } : totalCost > 500000 ? {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '4px',
    height: '100%',
    background: 'linear-gradient(180deg, #ff9200 0%, #ffb84d 100%)',
    borderRadius: '8px 0 0 8px'
  } : null;

  return (
    <Card 
      className="facility-card"
      style={{ 
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(0, 0, 0, 0.05)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden'
      }}
      onClick={onClick}
    >
      {priorityIndicator && <div style={priorityIndicator} />}
      
      <div className="facility-card-content">
        {/* Header Section */}
        <div className="facility-header" style={{ marginBottom: '16px' }}>
          <div className="facility-info">
            <Title level={4} style={{ 
              margin: 0, 
              color: '#1a1a1a',
              fontWeight: 700,
              lineHeight: 1.2,
              fontSize: '16px'
            }}>
              {batch.name}
            </Title>
            <Text style={{ 
              color: '#666', 
              fontSize: '13px', 
              fontWeight: 500,
              marginTop: '4px',
              display: 'block'
            }}>
              {batch.city}, {batch.state}
            </Text>
            {batch.payment_batch && (
              <div style={{ 
                marginTop: '8px',
                padding: '4px 8px',
                background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
                borderRadius: '12px',
                display: 'inline-block'
              }}>
                <Text style={{ 
                  color: 'white', 
                  fontSize: '11px', 
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Batch: {batch.payment_batch}
                </Text>
              </div>
            )}
          </div>
        </div>

        {/* Contact Information */}
        <div className="facility-contact" style={{ marginBottom: '16px' }}>
          <Row gutter={[12, 12]}>
            <Col span={24}>
              <div className="contact-item">
                <div className="contact-content">
                  <MailOutlined style={{ marginRight: '8px', fontSize: '14px', color: '#666' }} />
                  <Text style={{ color: '#666', fontWeight: 500, fontSize: '13px' }}>
                    {batch.email}
                  </Text>
                </div>
              </div>
            </Col>
            <Col span={24}>
              <div className="contact-item">
                <div className="contact-content">
                  <PhoneOutlined style={{ marginRight: '8px', fontSize: '14px', color: '#666' }} />
                  <a 
                    href={`tel:${batch.phone}`}
                    style={{ 
                      color: '#1890ff', 
                      textDecoration: 'none',
                      fontWeight: 500,
                      fontSize: '13px',
                      transition: 'opacity 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                  >
                    {batch.phone}
                  </a>
                </div>
              </div>
            </Col>
          </Row>
        </div>

        {/* Statistics Section */}
        <div className="facility-stats" style={{ marginBottom: '16px' }}>
          <Row gutter={[12, 12]}>
            <Col span={12}>
              <div className="stat-item">
                <div className="stat-icon">
                  <FileTextOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
                </div>
                <div className="stat-content">
                  <Statistic 
                    value={claims.length}
                    valueStyle={{ 
                      fontSize: '16px', 
                      fontWeight: 700, 
                      color: '#1890ff' 
                    }}
                  />
                  <Text style={{ fontSize: '10px', color: '#666', fontWeight: 600 }}>
                    Claims
                  </Text>
                </div>
              </div>
            </Col>
            <Col span={12}>
              <div className="stat-item">
                <div className="stat-icon">
                  <DollarOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
                </div>
                <div className="stat-content">
                  <Statistic 
                    value={totalCost}
                    precision={2}
                    prefix="₦"
                    valueStyle={{ 
                      fontSize: '16px', 
                      fontWeight: 700, 
                      color: '#52c41a' 
                    }}
                  />
                  <Text style={{ fontSize: '10px', color: '#666', fontWeight: 600 }}>
                    Total Cost
                  </Text>
                </div>
              </div>
            </Col>
            {approvedCost > 0 && (
              <Col span={12}>
                <div className="stat-item">
                  <div className="stat-icon">
                    <CheckOutlined style={{ color: '#722ed1', fontSize: '16px' }} />
                  </div>
                  <div className="stat-content">
                    <Statistic 
                      value={approvedCost}
                      precision={2}
                      prefix="₦"
                      valueStyle={{ 
                        fontSize: '16px', 
                        fontWeight: 700, 
                        color: '#722ed1' 
                      }}
                    />
                    <Text style={{ fontSize: '10px', color: '#666', fontWeight: 600 }}>
                      Approved Cost
                    </Text>
                  </div>
                </div>
              </Col>
            )}
            <Col span={approvedCost > 0 ? 12 : 12}>
              <div className="stat-item">
                <div className="stat-icon">
                  <TeamOutlined style={{ color: '#722ed1', fontSize: '16px' }} />
                </div>
                <div className="stat-content">
                  <Statistic 
                    value={uniquePatients}
                    valueStyle={{ 
                      fontSize: '16px', 
                      fontWeight: 700, 
                      color: '#722ed1' 
                    }}
                  />
                  <Text style={{ fontSize: '10px', color: '#666', fontWeight: 600 }}>
                    Patients
                  </Text>
                </div>
              </div>
            </Col>
            <Col span={12}>
              <div className="stat-item">
                <div className="stat-icon">
                  <CalendarOutlined style={{ color: '#fa8c16', fontSize: '16px' }} />
                </div>
                <div className="stat-content">
                  <Text style={{ 
                    fontSize: '14px', 
                    fontWeight: 700, 
                    color: '#fa8c16',
                    lineHeight: 1.2
                  }}>
                    {lastPaymentDate ? 
                      new Date(lastPaymentDate).toLocaleDateString('en-NG', { 
                        month: 'short', 
                        day: 'numeric' ,
                        year: 'numeric'
                      }) : 
                      'N/A'
                    }
                  </Text><br></br>
                  <Text style={{ fontSize: '10px', color: '#666', fontWeight: 600 }}>
                    Payment Date
                  </Text>
                </div>
              </div>
            </Col>
          </Row>
        </div>

        {/* Action Buttons */}
        <div className="facility-actions" style={{ marginTop: '16px', textAlign: 'center' }}>
          <Space size="small">
            <Button 
              type="primary" 
              icon={<EyeOutlined />}
              size="small"
              style={{ 
                borderRadius: '8px',
                fontWeight: 600,
                height: '32px',
                padding: '0 16px',
                boxShadow: '0 2px 8px rgba(24, 144, 255, 0.25)',
                background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                border: 'none'
              }}
            >
              View Details
            </Button>
            {claims.length && parseInt(claims[0].claims_status) === 3 && claims[0].payment_batch && (
              <Button
                type="default"
                icon={<FileTextOutlined />}
                size="small"
                href={`${uri}documents/claims/payments/claims/${claims[0].payment_batch}.pdf`}
                target="_blank"
                style={{ 
                  borderRadius: '8px',
                  fontWeight: 600,
                  height: '32px',
                  padding: '0 12px',
                  color: '#52c41a',
                  border: '1px solid #52c41a',
                  background: 'transparent',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#52c41a';
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#52c41a';
                }}
              >
                Document
              </Button>
            )}
          </Space>
        </div>
      </div>
    </Card>
  );
};

const ClaimsAdminNewHook = () => {
  const user = JSON.parse(localStorage.getItem(config.key.user));
  const navigate = useNavigate();
  
  // Simplified state management - removed paymentSubSegment
  const [activeSegment, setActiveSegment] = useState(0);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [facilities, setFacilities] = useState([]);

  // Search handler with debouncing
  const handleSearch = (value) => {
    setSearchKeyword(value);
    // The actual search will be triggered by the useEffect or manual fetch
  };

  // StandardFilter integration
  const {
    filters,
    loading: filterLoading,
    activeFiltersCount,
    handleFilterChange,
    handleApplyFilters,
    handleClearFilters,
    resetFilters,
    getFormattedFilters
  } = useStandardFilter({
    initialFilters: {
      search: '',
      fromDate: null,
      toDate: null
      // month: null,
      // year: null,
      // facilityId: null,
      // minCost: null,
      // maxCost: null,
      // insuranceNo: ''
    },
    autoApply: true,
    debounceDelay: 500,
    onApplyFilters: async (filterValues) => {
      const formattedFilters = getFormattedFilters(filterValues);
      await fetchData(activeSegment, filterValues.search || '', 1, formattedFilters);
    },
    onClearFilters: async () => {
      setSearchKeyword('');
      await fetchData(activeSegment, '', 1, {});
    }
  });

  // Simplified payment data processing (stable reference for fetchData)
  const processPaymentData = useCallback((claimsData) => {
    const batches = Array.from(new Set(claimsData?.data?.map(c => c?.payment_batch)));
    const pendingClaims = NullSafe(claimsData?.data?.filter(cl => Number(cl?.claims_status) === 2));
    const paidClaims = NullSafe(claimsData?.data?.filter(cl => Number(cl?.claims_status) === 3));

    const pending = [];
    const paid = [];

    for (let batch of batches) {
      for (let facility of claimsData?.facilities || []) {
        if (batch) {
          const batchClaims = paidClaims.filter(c => c.payment_batch === batch && c.facility_id === facility.id);
          if (batchClaims.length) {
            paid.push({ ...facility, status: 'Paid', claims: batchClaims, payment_batch: batch });
          }
        } else {
          const batchClaims = pendingClaims.filter(c => c.facility_id === facility.id);
          if (batchClaims.length) {
            pending.push({ ...facility, status: 'Pending', claims: batchClaims });
          }
        }
      }
    }

    return { pending, paid };
  }, []);
  
  // Enhanced data fetching with filters
  const fetchData = useCallback(async (segment = activeSegment, search = '', page = 1, filterParams = {}) => {
    setLoading(true);
    try {
      let response;
      
      // Prepare payload with all filter parameters
      const payload = {
        segment,
        id: user?.id,
        page,
        pageSize: pagination.pageSize,
        search,
        ...filterParams
      };
      
      if (segment === 3) {
        // Use payment endpoint for paid segment - show all payment data
        response = await myRequestObj("aClaimsListPayment").post(payload);
        const sortedData = processPaymentData(response?.data);
        // Show both pending and paid batches together
        const allBatches = [...sortedData.pending, ...sortedData.paid];
        setData(allBatches);
        setPagination({ current: 1, pageSize: allBatches.length, total: allBatches.length });
      } else {
        // Use regular endpoint for other segments
        response = await myRequestObj('aClaimsListMOCUltra').post(payload);
        const body = response?.data;
        if (!body || Number(body.error) !== 0) {
          notification.error({ message: 'Error', description: body?.message || 'Failed to fetch claims data' });
          return;
        }
        const processedData = Object.values(body.data).map(facility => ({
          facility_id: facility.id,
          facility_name: facility.name,
          facility_address: facility.address,
          facility_state: facility.state,
          facility_city: facility.city,
          facility_phone: facility.phone,
          facility_email: facility.email,
          unique_insurance_count: facility.unique_insurance_count,
          claims_count: facility.claims_count,
          total_encounter_cost: facility.total_encounter_cost,
          last_claim_date: facility.last_claim_date,
          last_claim_id: facility.last_claim_id,
          pa_count: facility.pa_count,
          pass: facility.pass
        }));
        setData(processedData);
        setPagination({ 
          current: body.pagination.currentPage, 
          pageSize: body.pagination.perPage, 
          total: body.pagination.total 
        });
      }
    } catch (error) {
      notification.error({ message: 'Error', description: 'Failed to fetch data', placement: 'topRight' });
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [activeSegment, user?.id, pagination.pageSize, processPaymentData]);

  // Load facilities for dropdown
  const loadFacilities = useCallback(async () => {
    try {
      const response = await myRequestObj('aFacilitiesList').post({});
      if (response?.data?.data) {
        const facilityOptions = response.data.data.map(facility => ({
          label: facility.name,
          value: facility.id
        }));
        setFacilities(facilityOptions);
      }
    } catch (error) {
      console.error('Error loading facilities:', error);
    }
  }, []);

  // Enhanced segment change
  const handleSegmentChange = (segment) => {
    setActiveSegment(segment);
    setSearchKeyword('');
    // Reset filters state without triggering a request
    resetFilters();
    // Fetch data with new segment
    fetchData(segment, '', 1, {});
  };

  // Effects
  useEffect(() => {
    fetchData();
    // loadFacilities();
  }, [fetchData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Navigation handlers
  const handleFacilityClick = (facility) => {
    navigate(`/claims/filings/${btoa(facility.facility_id)}/${btoa(activeSegment)}`, { 
      state: { segment: activeSegment, facility: facility.facility_id } 
    });
  };

  const handleBatchClick = (batch) => {
    navigate(`/claims/filings/${btoa(batch.id)}/${btoa(activeSegment)}`, { 
      state: { segment: activeSegment, facility: batch.id } 
    });
  };

  // Build segment options
  const segmentOptions = Object.entries(STATUS_CONFIG).map(([key, cfg]) => ({
    label: (
      <div style={{ padding: '12px 16px', color: cfg.color, fontWeight: 'bold', fontSize: '1.1em', display: 'flex', alignItems: 'center', gap: '8px' }}>
        {cfg.icon}
        {cfg.text}
      </div>
    ),
    value: Number(key)
  }));

  return (
    <div className="claims-container">
      {/* Enhanced Header */}
      <div className="claims-header">
        <div className="header-content">
          <div className="header-main">
            <Title level={2} style={{ 
              margin: 0, 
              color: '#1a1a1a',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: window.innerWidth <= 768 ? '24px' : '32px'
            }}>
              <FileTextOutlined style={{ 
                color: '#1890ff', 
                fontSize: window.innerWidth <= 768 ? '24px' : '32px' 
              }} />
              <span style={{ 
                display: window.innerWidth <= 480 ? 'none' : 'inline',
                fontSize: 'inherit'
              }}>
                Claims Management Dashboard
              </span>
              <span style={{ 
                display: window.innerWidth <= 480 ? 'inline' : 'none',
                fontSize: 'inherit'
              }}>
                Claims Dashboard
              </span>
            </Title>
            <Text style={{ 
              color: '#666', 
              fontSize: window.innerWidth <= 768 ? '14px' : '16px',
              marginTop: '8px',
              display: 'block',
              lineHeight: 1.5
            }}>
              {window.innerWidth <= 480 
                ? 'Review and manage facility claims'
                : 'Review and manage facility claims with advanced filtering and real-time updates'
              }
            </Text>
          </div>
          <div className="header-stats">
            <Row gutter={[16, 16]}>
              <Col xs={12} sm={6} md={6} lg={6}>
                <div className="stat-card">
                  <Statistic 
                    title={
                      <span style={{ 
                        fontSize: window.innerWidth <= 768 ? '10px' : '12px',
                        fontWeight: 600,
                        color: '#666'
                      }}>
                        Total Claims
                      </span>
                    }
                    value={pagination.total} 
                    valueStyle={{ 
                      color: '#1890ff', 
                      fontSize: window.innerWidth <= 768 ? '18px' : '24px', 
                      fontWeight: 700 
                    }}
                    prefix={
                      <FileTextOutlined style={{ 
                        fontSize: window.innerWidth <= 768 ? '14px' : '16px',
                        marginRight: '4px'
                      }} />
                    }
                  />
                </div>
              </Col>
              <Col xs={12} sm={6} md={6} lg={6}>
                <div className="stat-card">
                  <Statistic 
                    title={
                      <span style={{ 
                        fontSize: window.innerWidth <= 768 ? '10px' : '12px',
                        fontWeight: 600,
                        color: '#666'
                      }}>
                        Active Facilities
                      </span>
                    }
                    value={data.length} 
                    valueStyle={{ 
                      color: '#52c41a', 
                      fontSize: window.innerWidth <= 768 ? '18px' : '24px', 
                      fontWeight: 700 
                    }}
                    prefix={
                      <RiHospitalFill style={{ 
                        fontSize: window.innerWidth <= 768 ? '14px' : '16px',
                        marginRight: '4px'
                      }} />
                    }
                  />
                </div>
              </Col>
              <Col xs={12} sm={6} md={6} lg={6}>
                <div className="stat-card">
                  <Statistic 
                    title={
                      <span style={{ 
                        fontSize: window.innerWidth <= 768 ? '10px' : '12px',
                        fontWeight: 600,
                        color: '#666'
                      }}>
                        Current Status
                      </span>
                    }
                    value={STATUS_CONFIG[activeSegment].text} 
                    valueStyle={{ 
                      color: STATUS_CONFIG[activeSegment].color, 
                      fontSize: window.innerWidth <= 768 ? '14px' : '18px', 
                      fontWeight: 700 
                    }}
                    prefix={
                      <span style={{ 
                        fontSize: window.innerWidth <= 768 ? '12px' : '14px',
                        marginRight: '4px'
                      }}>
                        {STATUS_CONFIG[activeSegment].icon}
                      </span>
                    }
                  />
                </div>
              </Col>
              <Col xs={12} sm={6} md={6} lg={6}>
                <div className="stat-card">
                  <Statistic 
                    title={
                      <span style={{ 
                        fontSize: window.innerWidth <= 768 ? '10px' : '12px',
                        fontWeight: 600,
                        color: '#666'
                      }}>
                        Last Updated
                      </span>
                    }
                    value={new Date().toLocaleTimeString('en-US', { 
                      hour: '2-digit', 
                      minute: '2-digit',
                      hour12: true 
                    })} 
                    valueStyle={{ 
                      color: '#666', 
                      fontSize: window.innerWidth <= 768 ? '12px' : '14px', 
                      fontWeight: 500 
                    }}
                    prefix={
                      <ClockCircleOutlined style={{ 
                        fontSize: window.innerWidth <= 768 ? '12px' : '14px',
                        marginRight: '4px'
                      }} />
                    }
                  />
                </div>
              </Col>
            </Row>
          </div>
        </div>
      </div>

      {/* Enhanced Status Segments */}
      <div className="claims-status-segments">
        <Segmented
          block
          options={segmentOptions}
          value={activeSegment}
          onChange={handleSegmentChange}
          style={{ 
            userSelect: 'none',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '12px',
            padding: '4px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}
        />
      </div>

      {/* Standard Filter Component */}
      <StandardFilter
        filters={CLAIMS_FILTERS.payment}
        onFilterChange={handleFilterChange}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        initialValues={filters}
        loading={filterLoading}
        collapsible={true}
        defaultExpanded={false}
        title="Advanced Filters"
        showActiveCount={true}
        showSearchIcon={true}
        style={{ marginBottom: '24px' }}
      />

      {/* Enhanced Search and Controls */}
      <div className="claims-controls">
        <Row gutter={[16, 16]} align="middle" justify="space-between">
          <Col xs={24} sm={12} md={8}>
            <div className="search-container">
              <Input
                placeholder={activeSegment === 3 ? "Search facilities, claims, or batch numbers..." : "Search facilities, claims, or insurance numbers..."}
                value={searchKeyword}
                onChange={(e) => handleSearch(e.target.value)}
                allowClear
                loading={loading}
                prefix={<SearchOutlined style={{ color: '#1890ff' }} />}
                style={{ 
                  borderRadius: '12px',
                  height: '44px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  border: '1px solid #e8e8e8',
                  transition: 'all 0.3s ease'
                }}
                onFocus={(e) => {
                  e.target.style.boxShadow = '0 4px 16px rgba(24, 144, 255, 0.15)';
                  e.target.style.borderColor = '#1890ff';
                }}
                onBlur={(e) => {
                  e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                  e.target.style.borderColor = '#e8e8e8';
                }}
              />
            </div>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <div className="controls-container">
              <Space size="middle">
                <Button 
                  onClick={() => fetchData(activeSegment, searchKeyword)}
                  icon={<ReloadOutlined />}
                  loading={loading}
                  style={{ 
                    borderRadius: '10px',
                    height: '44px',
                    padding: '0 20px',
                    fontWeight: 600,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                  }}
                >
                  Refresh
                </Button>
                <div className="results-info">
                  <Text type="secondary" style={{ fontSize: '12px', fontWeight: 500 }}>
                    {pagination.total > 0 ? (
                      `Showing ${((pagination.current - 1) * pagination.pageSize) + 1}-${Math.min(pagination.current * pagination.pageSize, pagination.total)} of ${pagination.total} items`
                    ) : 'No items found'}
                  </Text>
                </div>
              </Space>
            </div>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <div className="pagination-wrapper">
              {activeSegment !== 3 && data.length > 0 && (
                <div className="pagination-container">
                  <Pagination
                    current={pagination.current}
                    pageSize={pagination.pageSize}
                    total={pagination.total}
                    onChange={(page) => fetchData(activeSegment, searchKeyword, page)}
                    showSizeChanger={false}
                    disabled={loading}
                    // showTotal={(total, range) => {
                    //   if (window.innerWidth <= 768) {
                    //     return `${range[0]}-${range[1]}`;
                    //   }
                    //   return `${range[0]}-${range[1]} of ${total} items`;
                    // }}
                    size={window.innerWidth <= 768 ? "small" : "default"}
                    simple={window.innerWidth <= 480}
                    style={{ 
                      textAlign: window.innerWidth <= 768 ? 'center' : 'right',
                      width: '100%'
                    }}
                    className="modern-pagination"
                    itemRender={(page, type, originalElement) => {
                      if (window.innerWidth <= 480) {
                        if (type === 'page') {
                          return (
                            <Button
                              type={page === pagination.current ? 'primary' : 'default'}
                              size="small"
                              onClick={() => fetchData(activeSegment, searchKeyword, page)}
                              style={{
                                minWidth: '32px',
                                height: '32px',
                                borderRadius: '8px',
                                margin: '0 2px',
                                fontWeight: page === pagination.current ? 600 : 500
                              }}
                            >
                              {page}
                            </Button>
                          );
                        }
                        if (type === 'prev') {
                          return (
                            <Button
                              type="default"
                              size="small"
                              disabled={pagination.current === 1}
                              onClick={() => fetchData(activeSegment, searchKeyword, pagination.current - 1)}
                              icon={<span>‹</span>}
                              style={{
                                minWidth: '32px',
                                height: '32px',
                                borderRadius: '8px',
                                margin: '0 2px'
                              }}
                            />
                          );
                        }
                        if (type === 'next') {
                          return (
                            <Button
                              type="default"
                              size="small"
                              disabled={pagination.current >= Math.ceil(pagination.total / pagination.pageSize)}
                              onClick={() => fetchData(activeSegment, searchKeyword, pagination.current + 1)}
                              icon={<span>›</span>}
                              style={{
                                minWidth: '32px',
                                height: '32px',
                                borderRadius: '8px',
                                margin: '0 2px'
                              }}
                            />
                          );
                        }
                      }
                      return originalElement;
                    }}
                  />
                  {/* {window.innerWidth <= 768 && (
                    <div className="mobile-pagination-info">
                      <div className="mobile-quick-jumper">
                        <Text type="secondary" style={{ 
                          fontSize: '12px', 
                          fontWeight: 500,
                          display: 'block',
                          textAlign: 'center',
                          marginBottom: '8px',
                          color: '#666'
                        }}>
                          Go to page:
                        </Text>
                        <div className="quick-jumper-controls">
                          <Input
                            size="small"
                            placeholder="Page #"
                            style={{
                              width: '80px',
                              textAlign: 'center',
                              borderRadius: '8px',
                              marginRight: '8px'
                            }}
                            onPressEnter={(e) => {
                              const page = parseInt(e.target.value);
                              if (page && page > 0 && page <= Math.ceil(pagination.total / pagination.pageSize)) {
                                fetchData(activeSegment, searchKeyword, page);
                                e.target.value = '';
                              }
                            }}
                          />
                          <Button
                            size="small"
                            type="primary"
                            style={{
                              borderRadius: '8px',
                              fontSize: '12px',
                              height: '32px',
                              padding: '0 12px'
                            }}
                            onClick={(e) => {
                              const input = e.target.closest('.quick-jumper-controls').querySelector('input');
                              const page = parseInt(input.value);
                              if (page && page > 0 && page <= Math.ceil(pagination.total / pagination.pageSize)) {
                                fetchData(activeSegment, searchKeyword, page);
                                input.value = '';
                              }
                            }}
                          >
                            Go
                          </Button>
                        </div>
                      </div>
                      <Text type="secondary" style={{ 
                        fontSize: '12px', 
                        fontWeight: 500,
                        display: 'block',
                        textAlign: 'center',
                        marginTop: '8px'
                      }}>
                        Page {pagination.current} of {Math.ceil(pagination.total / pagination.pageSize)}
                      </Text>
                    </div>
                  )} */}
                </div>
              )}
            </div>
          </Col>
        </Row>
      </div>

      {/* Enhanced Content */}
      <div className="claims-content">
        {loading ? (
          <div className="loading-container">
            <Spin size="large" />
            <Text style={{ marginTop: '16px', color: '#666' }}>
              Loading {STATUS_CONFIG[activeSegment].text} claims...
            </Text>
          </div>
        ) : data.length > 0 ? (
          <div className="facility-cards-grid">
            {data.map((item, index) => (
              activeSegment === 3 ? (
                <PaymentBatchCard
                  key={`${item.id}-${item.payment_batch}-${index}`}
                  batch={item}
                  activeSegment={activeSegment}
                  onClick={() => handleBatchClick(item)}
                />
              ) : (
                <FacilityCard
                  key={item.facility_id}
                  facility={item}
                  activeSegment={activeSegment}
                  onClick={() => handleFacilityClick(item)}
                />
              )
            ))}
          </div>
        ) : (
          <div className="claims-empty-state">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <div>
                  <Title level={4} style={{ color: '#666', marginBottom: '8px' }}>
                    No Claims Found
                  </Title>
                  <Text type="secondary" style={{ fontSize: '14px' }}>
                    {searchKeyword 
                      ? `No claims found matching "${searchKeyword}" in the "${STATUS_CONFIG[activeSegment].text}" category.`
                      : `There are no claims in the "${STATUS_CONFIG[activeSegment].text}" category at the moment.`
                    }
                  </Text>
                </div>
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ClaimsAdminNewHook; 